import { useEffect } from "react";
import {
  isWidgetEventMessage,
  WidgetEventConnectSpecificExchangePayload,
  WidgetEventMessage,
  WidgetEventUpdateExchangePayload,
  WidgetEventUpdatePairPayload,
  WidgetWindowEvents,
} from "@cede/types";
import { TRADING_VIEW_SUPPORTED_EXCHANGES } from "@/constants/trading";
import { useOAuthSupportMap } from "@/hooks/exchangeAuthentication/useOAuthSupportMap";
import { useCreateAccountStore } from "@/store/accountStore";
import { STEPS, FLOW_TYPES, useConnectStore } from "@/store/connectStore";
import { useTradingViewStore } from "@/store/useTradingViewStore";

/**
 * It centralizes all the actions related to the widget events
 */
export const useWidgetEventListeners = () => {
  const { updateFormData: updateFormAccountData } = useCreateAccountStore();
  const { setActiveStep: setActiveConnectAccountStep, setOpenConnectModal, setFlowType } = useConnectStore();
  const { setExchange, setPairSymbol } = useTradingViewStore();
  const oAuthSupportMap = useOAuthSupportMap();

  const onConnectExchangeAccount = async (exchangeId: string) => {
    updateFormAccountData({
      selectedCex: exchangeId,
    });
    setActiveConnectAccountStep(oAuthSupportMap[exchangeId] ? STEPS.LinkAccount : STEPS.ApiKeys);
    setFlowType(FLOW_TYPES.READY_TO_USE);
    setOpenConnectModal(true);
  };

  /**
   * Listen to the widget events
   */
  useEffect(() => {
    const onMessage = (event: MessageEvent<WidgetEventMessage>) => {
      if (isWidgetEventMessage(event.data)) {
        if (event.data.type === WidgetWindowEvents.UPDATE_EXCHANGE) {
          const exchangeData = event.data.payload as WidgetEventUpdateExchangePayload;
          if (exchangeData && !TRADING_VIEW_SUPPORTED_EXCHANGES.includes(exchangeData.exchangeId)) {
            setExchange(null);
            return;
          }
          setExchange(exchangeData);
        }

        if (event.data.type === WidgetWindowEvents.UPDATE_PAIR) {
          const previousPairSymbol = useTradingViewStore.getState().pairSymbol;
          const payload = event.data.payload as WidgetEventUpdatePairPayload;
          if (payload?.pairSymbol !== previousPairSymbol?.pairSymbol) {
            setPairSymbol(payload);
          }
        }

        if (event.data.type === WidgetWindowEvents.CONNECT_SPECIFIC_EXCHANGE) {
          const payload = event.data.payload as WidgetEventConnectSpecificExchangePayload;
          if (!payload) return;
          onConnectExchangeAccount(payload.exchangeId);
        }

        if (event.data.type === WidgetWindowEvents.CONNECT_EXCHANGE) {
          setFlowType(FLOW_TYPES.READY_TO_USE);
          setOpenConnectModal(true);
        }
      }
    };

    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, []);
};
